import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { formatDate, formatDateTime } from '../utils/FormatDateTime';
import StatusBadge from '../components/StatusBadge';
import { MdCall, MdEmail, MdLocationOn, MdBusiness, MdPerson, MdSchedule, MdNotes, MdAdd, MdCallMissed, MdBlock } from 'react-icons/md';
import { FiUser, FiPhoneCall, FiPackage, FiPhoneIncoming, FiPhoneOutgoing } from 'react-icons/fi';
import { addLead, getLeadDetails, patchCallLog, patchLead, postCallLog, updateLead } from '../services/telecalling';
import { toast } from 'react-toastify';
import { getUserId } from '../helpers/local_storage_helpers';
import { History } from '@mui/icons-material';
import AddLeadForm from './AddLeadForm';
import ShowModal from '../utils/ShowModal';
import Button from '../components/buttons/Button';
import InputField from '../components/inputFields/InputField';
import { Package } from 'lucide-react';

const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'interested', label: 'Interested' },
    { value: 'converted', label: 'Converted' },
    { value: 'not_interested', label: 'Not Interested' },
    { value: 'failed', label: 'Failed' },
];

// Status color class map
const getStatusColorClass = (status) => {
    switch (status?.toLowerCase()) {
        case 'pending': return 'bg-yellow-100 text-yellow-700';
        case 'contacted': return 'bg-blue-100 text-blue-700';
        case 'interested': return 'bg-indigo-100 text-indigo-700';
        case 'converted': return 'bg-green-100 text-green-700';
        case 'not_interested':
        case 'failed': return 'bg-red-100 text-red-700';
        default: return 'bg-gray-100 text-gray-600';
    }
};

const TelecallingViewDetails = ({ lead }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});

    const [editingIndex, setEditingIndex] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editedNote, setEditedNote] = useState("");
    const [editedStatus, setEditedStatus] = useState("");
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleEditNote = (currentNote, currentStatus, currentId, index) => {
        setEditingId(currentId);
        setEditingIndex(index);
        setEditedNote(currentNote || "");
        setEditedStatus(currentStatus || "connected");
    };

    const handleSaveNote = async ({ item, index }) => {
        try {
            const res = await patchCallLog({
                id: item.id,
                data: {
                    note: editedNote,
                    status: editedStatus,
                },
            });

            if (res.status === 200) {
                toast.success("Call-log updated successfully");
                setData(prevData => {
                    const updatedCallLogs = [...prevData.call_logs];
                    updatedCallLogs[index] = {
                        ...updatedCallLogs[index],
                        note: editedNote,
                        status: editedStatus,
                    };
                    return { ...prevData, call_logs: updatedCallLogs };
                });
                setEditingIndex(null);
                setEditedNote("");
                setEditedStatus("");
            } else {
                toast.error("Failed to update call-log");
            }
        } catch (err) {
            toast.error("Error during call-log update");
        }
    };

    useEffect(() => {
        if (!lead) return;
        setEditingId(null);
        setEditingIndex(null);
        setEditedNote("");
        setEditedStatus("");
        fetchData();
    }, [lead]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getLeadDetails(lead.id);
            if (res.status === 200) {
                setData(res.data);
            }
            else toast.error("Unable to fetch leads data");
        } catch (err) {
            toast.error("Error fetching leads data.");
        } finally {
            setLoading(false);
        }
    };

    if (!lead) return null;

    const fullAddress = [
        lead.address_line1,
        lead.village,
        lead.district,
        lead.state,
        lead.pincode
    ].filter(Boolean).join(', ');

    const initialFormValues = {
        current_status: data.follow_up?.current_status || '',
        next_follow_up_date: data.follow_up?.next_follow_up_date?.slice(0, 16) || '',
        note: data.follow_up?.note || '',
    };

    const validationSchema = Yup.object({
        current_status: Yup.string().required(),
        next_follow_up_date: Yup.string(),
        note: Yup.string(),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const res = await patchLead(data.follow_up.id, values);
            if (res.status === 200) {
                toast.success("Follow-up updated successfully");
            } else {
                toast.error("Failed to update follow-up");
            }
        } catch (err) {
            toast.error("Error during follow-up update");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitCallLog = async (values, { setSubmitting }) => {
        try {
            const res = await postCallLog({
                called_by: getUserId(),
                note: values.note,
                status: values.status,
                follow_up: data.follow_up.id,
            });
            if (res.status === 201) {
                toast.success("Call-log added successfully");
                fetchData();
            } else {
                toast.error("Failed to adding call-log");
            }
        } catch (err) {
            toast.error("Error during call-log");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddLeadSubmit = async (values) => {
        const transformedValues = {
            ...values,
            products: values.products.map(p => p.value),
        };

        setIsSubmitting(true);
        try {
            const response = lead ? await updateLead(lead.id, transformedValues) : await addLead({ ...transformedValues, added_by: getUserId() });

            if (response?.status === 201 || response?.status === 200) {
                fetchData();
                toast.success(lead ? "Lead updated successfully" : "Lead Added successfully!");
                setShowAddLeadModal(false);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setIsSubmitting(false);
    };

    return (
        <div className="space-y-4 text-sm">
            {/* <div className="fixed bottom-8 right-0 p-4 z-50">
                <Button
                    variant="primary"
                    onClick={() => setShowAddLeadModal(true)}
                    icon={<MdAdd size={16} />}
                    className="text-sm px-3 py-1.5 border rounded-md border-primary shadow-sm bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring focus:border-blue-300"
                >
                    Add Lead
                </Button>
            </div> */}
            <div className="flex justify-between items-start">
                <div>
                    <div className="flex flex-row gap-2">
                        <h2 className="text-lg font-semibold">{lead.name}</h2>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-600">
                            <StatusBadge status={lead.lead_source?.replace('_', ' ') || 'Unknown'} />
                            <StatusBadge status={lead.category} />
                            <span>Added on {formatDate(lead.created_at)}</span>
                        </div>
                    </div>
                    <div className="gap-2 flex flex-row mt-2">
                        <div className="flex items-center gap-2 text-sm bg-blue-50 p-2 rounded">
                            <MdCall className="text-primary" />
                            <span>{lead.phone}</span>
                            {lead.is_app_user && (
                                <span className="ml-auto text-green-600 text-xs bg-green-100 px-2 py-0.5 rounded">App User</span>
                            )}
                        </div>
                        {lead.email && (
                            <div className="flex items-center gap-2 bg-blue-50 p-2 rounded">
                                <MdEmail className="text-primary" />
                                <span>{lead.email}</span>
                            </div>
                        )}
                        {lead.shop_name && (
                            <div className="flex items-center gap-2 bg-blue-50 p-2 rounded">
                                <MdBusiness className="text-primary" />
                                <span>{lead.shop_name}</span>
                            </div>
                        )}
                        <div className="flex gap-2 bg-blue-50 p-2 rounded">
                            <MdLocationOn className="text-primary mt-1" />
                            <div className="text-sm">
                                <p className="text-gray-800">{fullAddress || '—'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                {/* Call Logs */}
                <div className="bg-white rounded shadow-sm">
                    <h3 className="text-lg font-medium mb-2 flex items-center gap-2">
                        <FiPhoneCall className="text-blue-500" /> Call Logs ({data.total_calls || 0})
                    </h3>
                    <Formik
                        initialValues={{
                            "note": "",
                            "status": "connected",
                        }}
                        validationSchema={Yup.object({
                            note: Yup.string(),
                        })}
                        enableReinitialize
                        onSubmit={handleSubmitCallLog}
                    >
                        {({ isSubmitting, handleChange, values }) => (
                            <Form>
                                <div className="gap-3">
                                    <div className="bg-white mb-2 rounded shadow-sm">
                                        <label className="text-gray-600 mb-2">Add Call-log Note</label>
                                        <div className="border-l-4 border-blue-400 px-2 mt-2 py-1 bg-blue-50 text-sm gap-3">
                                            <InputField
                                                name="note"
                                                placeholder="For Enter note..."
                                                className="p-2 rounded border mb-2 flex-1 w-full"
                                            />
                                            {/* <FormikDropdown name="status" options={[
                                                { value: 'connected', label: 'Connected' },
                                                { value: 'not_connected', label: 'Not Connected' }
                                            ]} placeholder="Connected/ Not Connected" /> */}
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="text-xs text-white bg-primary px-2 py-1 rounded"
                                            >
                                                {isSubmitting ? "Saving..." : "Save Extra Note"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                    {data.call_logs?.length > 0 ? (
                        <div className="space-y-2 text-xs">
                            {data.call_logs.map((log, i) => {
                                const isEditing = editingIndex === i;

                                // Map call_type to icon and color
                                const getCallTypeIcon = (type) => {
                                    switch ((type || "").toLowerCase()) {
                                        case "incoming":
                                        case "wifiincoming":
                                            return <FiPhoneIncoming className="text-green-500 rotate-90" title="Incoming" />;
                                        case "outgoing":
                                        case "wifioutgoing":
                                            return <FiPhoneOutgoing className="text-blue-500 -rotate-90" title="Outgoing" />;
                                        case "missed":
                                            return <MdCallMissed className="text-red-500" title="Missed" />;
                                        case "voicemail":
                                            return <MdCall className="text-yellow-500" title="Voicemail" />;
                                        case "rejected":
                                            return <MdCall className="text-gray-400" title="Rejected" />;
                                        case "blocked":
                                            return <MdBlock className="text-black" title="Blocked" />;
                                        case "answeredexternally":
                                            return <MdCall className="text-purple-500" title="Answered Externally" />;
                                        default:
                                            return <MdCall className="text-gray-300" title="Other" />;
                                    }
                                };

                                // Format duration in minutes
                                const renderDuration = (duration) => {
                                    if (typeof duration === "number" && !isNaN(duration)) {
                                        return (
                                            <span className="ml-2 text-xs text-gray-500">
                                                {duration} min{duration === 1 ? "" : "s"}
                                            </span>
                                        );
                                    }
                                    return null;
                                };

                                return (
                                    <div key={i} className="border-l-4 border-blue-400 pl-2 py-1">
                                        <div className="flex justify-between items-center">
                                            <span className='flex flex-row gap-2 items-center'>
                                                {getCallTypeIcon(log.call_type)}
                                                <span className="flex flex-row gap-1 items-center">
                                                    <p className="font-medium">{log.called_by}</p>
                                                    <p className={`${log.status === "connected" ? "text-primary" : ""}`}>({log.status})</p>
                                                    {renderDuration(log.duration)}
                                                </span>
                                            </span>
                                            <span className="text-gray-400">{formatDateTime(log.call_time)}</span>
                                        </div>

                                        <div className="bg-blue-50 p-2 mt-1 rounded relative space-y-1">
                                            {isEditing ? (
                                                <>
                                                    <textarea
                                                        className="w-full border border-gray-300 rounded p-1 text-xs"
                                                        rows={2}
                                                        value={editedNote}
                                                        onChange={(e) => setEditedNote(e.target.value)}
                                                    />
                                                    {/* <Dropdown
                                                        options={[
                                                            { value: 'in_progress', label: 'In Progress' },
                                                            { value: 'connected', label: 'Connected' },
                                                            { value: 'not_connected', label: 'Not Connected' },
                                                        ]}
                                                        value={editedStatus}
                                                        onChange={(e) => setEditedStatus(e)}
                                                        className="w-full text-sm"
                                                        compact
                                                    /> */}
                                                    <button
                                                        className="text-xs text-white bg-primary px-2 py-1 rounded"
                                                        onClick={() =>
                                                            handleSaveNote({
                                                                item: log,
                                                                index: i,
                                                            })
                                                        }
                                                    >
                                                        Save
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <p>{log.note}</p>
                                                    <button
                                                        className={`absolute top-1 right-1 text-gray-400 hover:text-blue-600 ${i > 1 && "hidden"}`}
                                                        onClick={() => handleEditNote(log.note, log.status, log.id, i)}
                                                    >
                                                        ✎
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-center text-gray-400 py-4">No call logs available</p>
                    )}
                </div>
                <div className="bg-white rounded shadow-sm">
                    <div className="bg-white p-3 rounded shadow-sm">
                        <h3 className="text-lg font-medium mb-2 flex gap-3"><History className="text-blue-500 text-sm" /> Activity Summary</h3>
                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { label: 'Total Calls', value: data.total_calls },
                                { label: 'Follow-ups', value: data.follow_up?.follow_up_count },
                            ].map((item, i) => (
                                <div key={i} className="flex flex-row items-center justify-between bg-blue-50 p-2 rounded mb-1 text-xs">
                                    <span>{item.label}</span>
                                    <span className="font-bold text-primary text-lg">{item.value || 0}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <Formik
                        initialValues={initialFormValues}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting, handleChange, values }) => (
                            <Form>
                                <div className="gap-3">
                                    <div className="bg-white rounded shadow-sm">
                                        <h3 className="text-lg font-medium mb-2 flex items-center gap-2"><FiUser className="text-blue-500" /> Follow-Up Info</h3>
                                        <div className="space-y-2">
                                            <div className="flex flex-col text-sm">
                                                <label className="text-gray-600 mb-1">Current Status</label>
                                                <Field
                                                    as="select"
                                                    name="current_status"
                                                    className={`p-2 rounded border ${getStatusColorClass(values.current_status)}`}
                                                >
                                                    <option value="">Select status</option>
                                                    {statusOptions.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </Field>
                                            </div>

                                            <div className="flex flex-col text-sm">
                                                <label className="text-gray-600 mb-1">Next Follow-up</label>
                                                <Field
                                                    type="date"
                                                    name="next_follow_up_date"
                                                    className="p-2 rounded border bg-blue-50"
                                                />
                                            </div>

                                            <div className="flex flex-col text-sm">
                                                <label className="text-gray-600 mb-1">Follow-up Note</label>
                                                <Field
                                                    as="textarea"
                                                    name="note"
                                                    rows={2}
                                                    placeholder="Enter note..."
                                                    className="p-2 rounded border bg-blue-50"
                                                />
                                            </div>
                                        </div>

                                        <div className="text-right mt-3">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="px-4 py-1.5 bg-primary text-white rounded hover:bg-primary/45"
                                            >
                                                {isSubmitting ? "Saving..." : "Save Follow-Up"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                    <h3 className="text-lg font-medium mb-2 flex items-center gap-2"><Package className="text-blue-500" /> Products</h3>
                    <div className="grid grid-cols-3 gap-3 mt-3">
                        {
                            lead.products.map((product, i) => (
                                <div className="gap-2 bg-blue-50 p-2 rounded">
                                    <img src={product.product_image} alt={product.product_image} className='aspect-square w-full h-fit rounded-xs' />
                                    <div className="text-sm">
                                        <p className="text-gray-800 line-clamp-3">{product.product_name || '—'}</p>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
            <div className="mt-4">
                <AddLeadForm
                    onSubmit={handleAddLeadSubmit}
                    onCancel={() => { setShowAddLeadModal(false); }}
                    isSubmitting={isSubmitting}
                    initialValues={lead || {}}
                />
            </div>
            <ShowModal
                contentLabel="Add New Lead"
                isOpen={showAddLeadModal}
                modalWidth="60%"
                onRequestClose={() => { setShowAddLeadModal(false); }}
                componentToPass={
                    <AddLeadForm
                        onSubmit={handleAddLeadSubmit}
                        onCancel={() => { setShowAddLeadModal(false); }}
                        isSubmitting={isSubmitting}
                        initialValues={data || {}}
                    />
                }
            />
        </div >
    );
};

export default TelecallingViewDetails;
